import { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Grid,
  TableContainer,
  Tooltip,
  Button,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import PageTitle from "../../molecules/PageTitle";
import UpdateButton from "../../atoms/Button/UpdateButton";
import {
  CustomContainer,
  StyledDataGrid,
  customDataGridLocaleTextAll,
  customDataGridLocaleTextLoading,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import SettingsIcon from "@mui/icons-material/Settings";
import LogIcon from "@mui/icons-material/Book";
import MonitorIcon from "@mui/icons-material/Monitor";
import IconButtonWithTooltip from "../../atoms/Button/IconButtonWithTooltip";
import NavigateButton from "../../atoms/Button/NavigateButton";
import { CircleOutlined, Close } from "@mui/icons-material";

interface Door {
  autoSdcId: number;
  sdcSerialNumber: string;
  isOnline: boolean;
  doorPlace: string;
  isEnabled: boolean;
  memo: string;
  building: {
    autoBuildingId: number;
    buildingId: string;
    buildingManagementId: string;
    buildingName: string;
    buildingAddress: string;
  };
}

const DoorListPage = () => {
  const navigate = useNavigate();
  const { autoBuildingId } = useParams();
  const [doors, setDoors] = useState<Door[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 内部の非同期関数を定義
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call indexSdcsWithAutoBuildingId API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT +
            `/buildings/${autoBuildingId}/sdcs/`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          }
        );

        const data = await response.json();
        console.log(data);
        setDoors(data.sdcs);
        setIsLoading(false);
      } catch (error) {
        console.error("Error:", error);
        setIsLoading(false);
      }
    };

    // 非同期関数を実行
    fetchData();
  }, [autoBuildingId]);

  const handleDoorSettingsClick = (autoSdcId: number) => {
    navigate(`/buildings/${autoBuildingId}/doors/${autoSdcId}`, {
      state: autoSdcId,
    });
    console.log(`設定 button clicked 選択されたドア: ${autoSdcId}`);
  };

  const handleDoorLogClick = (autoSdcId: number) => {
    navigate(`/buildings/${autoBuildingId}/doors/${autoSdcId}/log`, {
      state: autoSdcId,
    });
    console.log(`ログ button clicked 選択されたドア: ${autoSdcId}`);
  };

  const columns: GridColDef[] = [
    {
      field: "sdcSerialNumber",
      headerName: "SDC SN",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "isOnline",
      headerName: "接続状態",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value ? "オンライン" : "オフライン"}>
          {params.value ? (
            <CircleOutlined sx={{ color: "green" }} />
          ) : (
            <Close sx={{ color: "red" }} />
          )}
        </Tooltip>
      ),
    },
    {
      field: "doorPlace",
      headerName: "設置場所",
      flex: 1.5,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "memo",
      headerName: "メモ",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "action",
      headerName: "操作",
      sortable: false,
      flex: 1,
      renderCell: (params: { row: { autoSdcId: any } }) => {
        const autoSdcId = params.row.autoSdcId;
        return (
          <>
            <IconButtonWithTooltip
              title="設定・操作"
              onClick={() => handleDoorSettingsClick(autoSdcId)}
              icon={<SettingsIcon />}
            />
            <IconButtonWithTooltip
              title="ログ"
              onClick={() => handleDoorLogClick(autoSdcId)}
              icon={<LogIcon />}
            />
          </>
        );
      },
    },
  ];

  const rows = doors.map((door, index) => {
    return {
      id: index,
      sdcSerialNumber: door.sdcSerialNumber,
      isOnline: door.isOnline,
      memo: door.memo,
      doorPlace: door.doorPlace,
      isEnabled: door.isEnabled,
      building: door.building,
      autoSdcId: door.autoSdcId,
    };
  });

  return (
    <CustomContainer maxWidth={false}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <PageTitle title="管理ドア一覧" />
        <NavigateButton to={"/buildings"} buttonTitle={"管理物件一覧"} />
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        marginBottom={2}
      >
        <Box maxWidth="lg">
          <TableContainer>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" scope="row">
                    物件システムID:
                  </TableCell>
                  <TableCell>{rows[0]?.building.buildingId || ""}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    取付先コード（物件管理ID）:
                  </TableCell>
                  <TableCell>
                    {rows[0]?.building.buildingManagementId || ""}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    取付先名（物件名称）:
                  </TableCell>
                  <TableCell>{rows[0]?.building.buildingName || ""}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" scope="row">
                    物件住所:
                  </TableCell>
                  <TableCell>
                    {rows[0]?.building.buildingAddress || ""}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Grid item xs={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="contained"
              color="primary"
              disabled={doors.length === 0}
              endIcon={<MonitorIcon />}
              onClick={() =>
                window.open(
                  `/buildings/${autoBuildingId}/assistance-monitor`,
                  "_blank"
                )
              }
            >
              介助要請モニタ
            </Button>
            <UpdateButton />
          </Box>
        </Grid>
      </Box>
      <div style={{ height: 700, width: "100%" }}>
        <StyledDataGrid
          rows={rows}
          columns={columns}
          // checkboxSelection
          localeText={
            isLoading
              ? customDataGridLocaleTextLoading
              : rows.length === 0
              ? customDataGridLocaleTextNoData
              : customDataGridLocaleTextAll
          }
        />
      </div>
    </CustomContainer>
  );
};

export default DoorListPage;
